/**
 * Guía: Monitoreo Operativo.
 *
 * Contenido derivado del capítulo **07** del manual —§0 (en qué se diferencia
 * del monitoreo del servidor, y que la misma ruta sirve dos paneles según el
 * rol), §4 (las ocho secciones y de dónde sale cada número), §4.2 (las dos
 * alertas y su umbral) y §5 (el refresco)— y del flujo F43, que es el que se
 * le sirve a quien atiende.
 *
 * El paso de los pagos fallidos es el que evita un error caro: la lista
 * enseña diez y el total puede ser mucho mayor. La alerta cuenta el total;
 * quien mire sólo la lista minimizará justo lo que había que escalar.
 */

import type { TourFlow } from '../types';

const MONITOREO_PATH = '/monitoring';

export const c07MonitoreoOperativo: TourFlow = {
  id: 'admin.monitoring',
  chapter: '07',
  title: 'Monitoreo operativo',
  summary:
    'Cómo va el país ahora mismo: qué dispara cada alerta, qué se refresca solo y qué cifra hay que mirar.',
  href: MONITOREO_PATH,
  requiresCountry: true,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'Cómo va el negocio, ahora mismo',
      body:
        'Cuántos pedidos entraron, cuánto se facturó, qué pagos fallaron, qué tickets están ' +
        'abiertos y qué comercios se mueven. No confundir con Monitoreo del Servidor, que ' +
        'responde otra pregunta: si la infraestructura aguanta.',
      route: MONITOREO_PATH,
    },
    {
      id: 'needs-country',
      title: 'Sin país no hay nada que mostrar',
      body:
        'Es la primera pantalla de la sección de país y depende de él por completo. Si la ' +
        'ves vacía, lo primero es comprobar el selector: no está rota, le falta el país.',
    },
    {
      id: 'two-panels',
      title: 'La misma dirección enseña dos paneles',
      body:
        'El administrador general ve el panel completo; el responsable de soporte, una ' +
        'versión centrada en tickets. No es un panel con partes escondidas, son dos. Por ' +
        'eso «a mí no me sale esa gráfica» suele ser el rol y no un fallo.',
    },
    {
      id: 'alerts',
      title: 'Dos alertas, y sus umbrales',
      body:
        'Aparecen sólo cuando hay algo que mirar: un ticket crítico abierto basta, y los ' +
        'pagos fallidos avisan a partir de cinco en el periodo. Que no haya banner es la ' +
        'situación normal, no una pantalla incompleta. Cada alerta lleva al listado donde ' +
        'se trabaja: el banner avisa, no resuelve.',
      target: 'admin.monitoring.alerts',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'kpis',
      title: 'Las cuatro cifras, y a dónde llevan',
      body:
        'Pedidos, ingresos, tickets y tiendas activas del periodo elegido. Son clicables: ' +
        'cada una abre el listado que la explica, que es más rápido que reconstruir el ' +
        'filtro a mano.',
      target: 'admin.monitoring.kpis',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'ten-of-many',
      title: 'La lista de fallidos enseña diez',
      body:
        'Los listados de pagos y notificaciones fallidas muestran los diez más recientes y ' +
        'dicen cuántos hay en total. **El número que importa es el total.** Leer la longitud ' +
        'de la lista minimiza justo lo que había que escalar.',
    },
    {
      id: 'pipeline-is-now',
      title: 'Los pedidos por estado son los abiertos ahora',
      body:
        'No los del periodo: los completados y los cancelados no salen ahí. Es una foto de ' +
        'lo que está en marcha, así que compararlo con el total de pedidos del rango da ' +
        'distinto y las dos cosas son correctas.',
    },
    {
      id: 'today-is-local',
      title: '«Hoy» empieza a medianoche en el país',
      body:
        'El periodo se resuelve en la zona horaria del país seleccionado, no en la tuya. ' +
        'Consultando desde otro huso, «hoy» no es el mismo día que en la pantalla — y la ' +
        'pantalla tiene razón.',
      target: 'admin.monitoring.range',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'refresh',
      title: 'Se refresca solo, pero no todo',
      body:
        'Las cifras de arriba se actualizan cada minuto. Las gráficas y los listados ' +
        'destacados no: ésos se recargan al cambiar de país o de rango, porque cuestan ' +
        'mucho más de calcular. Una cifra que se mueve sola y una gráfica que no, no es un ' +
        'error.',
    },
    {
      id: 'not-for-closing',
      title: 'Estas cifras son de vigilancia, no de cierre',
      body:
        'Sirven para ver qué pasa ahora. Para cuadrar dinero están el panel financiero y ' +
        'los informes, que cuentan otra cosa y sobre otro periodo. No se concilia con lo ' +
        'que se ve aquí.',
    },
  ],
};
